<template>
  <div class="relative w-full">
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md">
      <!-- Header with Export Buttons -->
      <div v-if="showExportBtns && !isPrinting" class="px-4 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
        <!-- Applied Filters -->
        <div v-if="showAppliedFilters && appliedFilters.length > 0" class="flex-1 min-w-[200px]">
          <div class="text-xs text-gray-500 mb-1">فیلترهای اعمال شده:</div>
          <div class="flex flex-wrap gap-1">
            <div
                v-for="(filter, i) in appliedFilters"
                :key="i"
                class="flex items-center bg-gray-100 rounded-full px-2 py-1 text-xs"
            >
              <span class="text-gray-700">{{ filter.text }}</span>
              <span v-if="filter.typeText" class="text-gray-500 mx-1">({{ filter.typeText }})</span>
              <span class="font-medium text-primary-600">{{ filter.value }}</span>
              <button
                  @click="clearSpecificFilters(filter['text'])"
                  class="text-gray-400 hover:text-red-500 ml-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Export Buttons -->
        <div class="flex items-center gap-2">
          <button
              v-if="serverProcessing"
              @click="getDataFromApi"
              class="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              title="دریافت دوباره اطلاعات"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          <button
              @click="exportTo('html')"
              class="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
              title="چاپ"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>

          <button
              @click="exportTo('pdf')"
              class="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              title="خروجی PDF"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </button>

          <button
              @click="exportTo('xlsx')"
              class="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
              title="خروجی اکسل"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Data Table -->
      <v-data-table
          :value="selectedValues"
          :fixed-header="fixed_header"
          :height="height"
          :id="'datatable-' + _uid"
          ref="datatable"
          class="rounded-b-lg"
          :show-select="!isPrinting && showSelect"
          :headers="headersWithFilter"
          :header-props="{
          sortIcon: 'fa-sort-up',
          class: 'bg-gray-50 text-gray-700 font-semibold'
        }"
          :items="tableItems"
          :options.sync="options"
          :server-items-length="totalItems"
          :loading="loading"
          @input="(v) => (selectedItems = v)"
          :search="search"
          :disable-pagination="isPrinting"
          :disable-sort="isPrinting"
          :hide-default-footer="isPrinting"
          :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [15, 25, 50, 100, 200],
          class: 'px-4 py-2 border-t border-gray-200 bg-gray-50'
        }"
          v-on="$listeners"
          v-bind="$attrs"
          :mobile-breakpoint="0"
          :loading-text="'در حال دریافت اطلاعات...'"
          :no-data-text="'داده‌ای یافت نشد'"
          :no-results-text="'نتیجه‌ای یافت نشد'"
      >
        <!-- Row Number -->
        <template #item.rowNumber="{ item }">
          <span class="text-xs text-gray-500">{{ getRowNumber(item) }}</span>
        </template>

        <!-- Detail Button -->
        <template #item.detail="{ item }">
          <button
              @click="$emit('detail', item)"
              class="p-1 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </template>

        <!-- Filter Menu for Headers -->
        <template v-for="header in headersWithFilter" v-slot:[getHeaderSlot(header.value)]="{ header }">
          <template v-if="header.filterable != false && !isPrinting">
            <div class=" items-center ">
              <span class=" text-xs font-medium text-gray-700 text-center ml-1">{{ header.text }}</span>

              <v-menu
                  top
                  left
                  offset-y
                  :close-on-content-click="false"
                  :key="header.value"
                  v-model="filterMenus[header.value]"
              >
                <template v-slot:activator="{ on, attrs }">
                  <button
                      v-bind="attrs"
                      v-on="on"
                      @click.stop
                      class="p-1 rounded-md hover:bg-gray-100 transition-colors"
                      :class="{'text-indigo-600': filters[header.value]}"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                  </button>
                </template>

                <div class="bg-white rounded-lg shadow-lg border border-gray-200 w-64 overflow-hidden">
                  <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                    <span class="text-sm font-medium text-gray-700">فیلتر {{ header.text }}</span>
                    <button @click="filterMenus[header.value] = false" class="text-gray-400 hover:text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  <div class="p-4 space-y-3">
                    <template v-if="isBoolean(header)">
                      <v-autocomplete
                          color="primary"
                          :label="header.text"
                          :items="getBooleanFilterItems(header)"
                          clearable
                          :value="filters[`${header.value}`]"
                          @input="emitFilter(`${header.value}`, $event)"
                          outlined
                          dense
                          hide-details
                      />
                    </template>

                    <template v-else-if="!header.items">
                      <component
                          color="primary"
                          v-if="getFilterField(header) == 'date'"
                          :is="getFilterField(header)"
                          :label="`در${serverProcessing ? '' : ''}`"
                          :value="filters[`${header.value}`]"
                          @input="emitFilter(`${header.value}`, $event)"
                          clearable
                          outlined
                          dense
                          hide-details
                      />

                      <component
                          color="primary"
                          v-else-if="getFilterField(header) == 'mtime'"
                          :is="getFilterField(header)"
                          :label="`در${serverProcessing ? '' : ''}`"
                          :value="filters[`${header.value}`]"
                          @input="emitFilter(`${header.value}`, $event)"
                          clearable
                          outlined
                          dense
                          hide-details
                      />

                      <component
                          color="primary"
                          v-else-if="getFilterField(header) == 'money'"
                          :is="getFilterField(header)"
                          :label="`برابر با${serverProcessing ? '' : ''}`"
                          :value="filters[`${header.value}`]"
                          @input="emitFilter(`${header.value}`, $event)"
                          clearable
                          outlined
                          dense
                          hide-details
                      />

                      <component
                          color="primary"
                          v-else
                          :is="getFilterField(header)"
                          :label="`جستجو${serverProcessing ? 'ی دقیق' : ''}`"
                          :value="filters[`${header.value}`]"
                          @input="emitFilter(`${header.value}`, $event)"
                          clearable
                          outlined
                          dense
                          hide-details
                      />

                      <template v-if="serverProcessing">
                        <component
                            color="primary"
                            v-if="!showFilterRow"
                            :is="getFilterField(header)"
                            label="جستجو"
                            :value="filters[`${header.value}__icontains`]"
                            @input="emitFilter(`${header.value}__icontains`, $event)"
                            clearable
                            outlined
                            dense
                            hide-details
                        />
                      </template>

                      <template v-if="hasRangeFilter(header)">
                        <div class="grid grid-cols-2 gap-2">
                          <component
                              color="primary"
                              :is="getFilterField(header)"
                              label="از"
                              :value="filters[`${header.value}__gte`]"
                              @input="emitFilter(`${header.value}__gte`, $event)"
                              clearable
                              outlined
                              dense
                              hide-details
                          />

                          <component
                              color="primary"
                              :is="getFilterField(header)"
                              label="تا"
                              :value="filters[`${header.value}__lte`]"
                              @input="emitFilter(`${header.value}__lte`, $event)"
                              clearable
                              outlined
                              dense
                              hide-details
                          />
                        </div>
                      </template>
                    </template>

                    <template v-else>
                      <v-autocomplete
                          color="primary"
                          :label="header.text"
                          :items="header.items"
                          clearable
                          :value="filters[`${header.value}`]"
                          @input="emitFilter(`${header.value}`, $event)"
                          outlined
                          dense
                          hide-details
                      />
                    </template>
                  </div>

                  <div class="px-4 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
                    <button
                        @click="clearFilters(header)"
                        class="px-3 py-1 text-xs rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-1"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      حذف فیلترها
                    </button>
                  </div>
                </div>
              </v-menu>
            </div>
          </template>

          <span v-else class="text-xs font-medium text-gray-700">{{ header.text }}</span>
        </template>

        <!-- Clear Filters Button -->
        <template #header.rowNumber v-if="showClearFiltersBtn">
          <button
              v-if="!isPrinting"
              @click="clearAllFilters"
              class="p-1 rounded-md hover:bg-gray-100 transition-colors text-gray-500 hover:text-red-500"
              title="خالی کردن فیلترها"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <span v-else>#</span>
        </template>

        <!-- Quick Filter Row -->
        <template #body.prepend v-if="showFilterRow">
          <tr class="bg-gray-50">
            <th :colspan="showSelect ? 2 : 1" class="px-4 py-2 text-xs font-medium text-center text-gray-700">
              فیـلـتــر سـریــع
            </th>

            <th v-for="header in headers" class="px-2 py-1" v-if="header.show != false">
              <div v-if="header.filterable != false" class="w-full">
                <v-autocomplete
                    v-if="isBoolean(header)"
                    color="primary"
                    :items="getBooleanFilterItems(header)"
                    :value="filters[`${header.value}`]"
                    @input="emitFilter(`${header.value}`, $event)"
                    outlined
                    dense
                    hide-details
                    class="w-full"
                />

                <component
                    v-else-if="!header.items"
                    color="primary"
                    :is="getFilterField(header)"
                    :value="filters[serverProcessing ? `${header.value}__icontains`: `${header.value}`]"
                    @input="emitFilter(serverProcessing ? `${header.value}__icontains`: `${header.value}`, $event)"
                    outlined
                    dense
                    hide-details
                    class="w-full"
                />

                <v-autocomplete
                    v-else
                    color="primary"
                    :items="header.items"
                    :value="filters[`${header.value}`]"
                    @input="emitFilter(`${header.value}`, $event)"
                    outlined
                    dense
                    hide-details
                    class="w-full"
                />
              </div>
            </th>
          </tr>

          <slot name="previous-remain-row"></slot>
        </template>

        <!-- Data Cells -->
        <template v-for="header in headers" v-slot:[getItemSlot(header.value)]="{ item }">
          <!-- Numeric Values -->
          <template v-if="isNumber(header)">
            <span
                class="text-xs font-medium text-right"
                :class="getNumericClasses(item, header.value)"
            >
              {{ getItemValue(item, header.value) | toMoney }}
            </span>
          </template>

          <!-- Select Values -->
          <template v-else-if="isSelect(header)">
            <span class="text-xs text-gray-700">{{ getSelectItemValue(header, item) }}</span>
          </template>

          <!-- Boolean Values -->
          <template v-else-if="isBoolean(header)">
            <span
                class="inline-flex items-center justify-center w-5 h-5 rounded-full"
                :class="getItemValue(item, header.value) ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'"
            >
              <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
              >
                <path
                    v-if="getItemValue(item, header.value)"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                />
                <path
                    v-else
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </span>
          </template>

          <!-- Date Values -->
          <template v-else-if="isDate(header)">
            <span class="text-xs text-gray-700 whitespace-nowrap">{{ getItemValue(item, header.value) }}</span>
          </template>

          <!-- Time Values -->
          <template v-else-if="isTime(header)">
            <span class="text-xs text-gray-700 whitespace-nowrap">{{ getItemValue(item, header.value) }}</span>
          </template>

          <!-- Other Values -->
          <template v-else>
            <truncate
                :max-length="(header.max_length) ? header.max_length : 30"
                :value="getItemValue(item, header.value)"
                class="text-xs text-gray-700"
            />
          </template>
        </template>

        <!-- Summary Rows -->
        <template v-if="apiResponse" v-slot:body.append="{ headers }">
          <template v-for="(sum, i) in [apiResponse.page_sum, apiResponse.sum]">
            <tr v-if="sum" class="bg-blue-50">
              <td v-for="header in headers" class="px-4 py-2 text-xs font-medium text-center">
                <template v-if="header.value == 'rowNumber'">
                  <span class="text-blue-600 font-semibold">
                    {{ i == 0 ? "جمع این صفحه" : "جمع کل" }}
                  </span>
                </template>
                <template v-else>
                  <span class="text-blue-600 font-semibold">
                    {{ sum[header.value] | toMoney }}
                  </span>
                </template>
              </td>
            </tr>
          </template>
        </template>

        <!-- Pass through all other slots -->
        <template v-for="(index, name) in $slots" v-slot:[name]>
          <slot :name="name"/>
        </template>
        <template v-for="(index, name) in $scopedSlots" v-slot:[name]="data">
          <slot :name="name" v-bind="data"></slot>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import XLSX from "xlsx";
import {jsPDF} from "jspdf";
import _ from "lodash";
import Truncate from "./Truncate";
import {VTextField} from "vuetify/lib";

export default {
  props: {
    height: {
      default: 750,
    },
    fixed_header: {
      default: true,
    },
    isDialog: {
      default: false,
    },
    showFilterRow: {
      default: true,
    },
    apiUrl: {
      default: null,
    },
    showExportBtns: {
      default: true,
    },
    exportUrl: {
      default: null,
    },
    headers: {
      required: true,
    },
    showSelect: {
      default: true,
    },
    showRowNumber: {
      default: true,
    },
    filters: {
      default() {
        return {};
      },
    },
    showAppliedFilters: {
      default: true,
    },
    showClearFiltersBtn: {
      default: true,
    },
    searchable: {
      default: true,
    },
    previousApiData: {
      default: null,
    },
    currentApiData: {
      default: null,
    },
    additionalAppliedFilters: {
      default: null,
    },
    items: {
      default() {
        return [];
      },
    },
  },
  components: {
    Truncate,
    VTextField,
  },
  data() {
    return {
      list_view: false,
      mydata: "",
      filterMenus: {},
      search: "",
      totalItems: -1,
      tableItems: this.items,
      selectedItems: [],
      selectedValues: [],
      loading: false,
      options: {},
      apiResponse: null,
      numericValues: ["bed", "bes", "value", "fee", "price", "count"],
      booleanValues: ["is_auto_created"],
      d: {},
    };
  },
  computed: {
    isXs() {
      return window.innerWidth <= 600;
    },
    hasDetail() {
      return this.$listeners && this.$listeners.detail;
    },
    filterTypes() {
      let filterTypes = [
        {
          getKey: (valueName) => valueName,
        },
        {
          key: "icontains",
        },
        {
          key: "gte",
        },
        {
          key: "lte",
        },
      ];
      for (let filterType of filterTypes) {
        if (!filterType.getKey) {
          filterType.getKey = (valueName) => `${valueName}__${filterType.key}`;
        }
      }
      return filterTypes;
    },
    headersWithFilter() {
      let filter = (propertyName) => {
        return (value) => {
          if (!this.filters[propertyName]) return true;
          return String(value).includes(this.filters[propertyName]);
        };
      };

      let headers = this.headers.filter((h) => {
        if (h.show == false) return false;
        if (this.isPrinting && h.hideInExport == true) return false;
        return true;
      });

      if (this.showRowNumber) {
        headers = [
          {
            text: "#",
            value: "rowNumber",
            width: "20px",
            align: "center",
            sortable: false,
            filterable: false,
          },
          ...headers.map((o) => {
            o.align = "center";
            return o;
          }),
        ];
      } else {
        headers = [
          ...headers.map((o) => {
            o.align = "center";
            return o;
          }),
        ];
      }

      if (this.hasDetail) {
        headers.push({
          text: "",
          value: "detail",
          sortable: false,
          filterable: false,
          visible: false,
        });
      }

      for (let header of headers) {
        header.filter = filter(header.value);
      }

      return headers;
    },
    serverProcessing() {
      return this.apiUrl != null;
    },
    serverExport() {
      return this.exportUrl != null;
    },
    appliedFilters() {
      let appliedFilters = [];

      if (this.additionalAppliedFilters) {
        appliedFilters.push(...this.additionalAppliedFilters);
      }

      for (let header of this.headers) {
        if (header.show == false && header.showInAppliedFilters != true) continue;
        Object.keys(this.filters)
            .filter((key) => key.replaceAll(".", "__").startsWith(header.value.replaceAll(".", "__")))
            .forEach((key) => {
              let value = this.filters[key];

              if ([null, undefined, ""].includes(value)) return;

              let filterTypesTexts = {
                "": null,
                icontains: "شامل",
                gte: "از",
                lte: "تا",
              };

              let keyParts = key.split("__");
              let filterType = keyParts[keyParts.length - 1];
              let filterTypeText = filterTypesTexts[filterType];

              if (this.isNumber(header)) value = this.toMoney(value);

              if (this.isSelect(header) && header.items.length) {
                let subValues = [];
                value.split(",").forEach((subValue) => {
                  let item = header.items.find((o) => o.value == subValue);
                  if (item) {
                    subValues.push(item.text);
                  } else {
                    console.error("Wrong select value", header, subValue);
                  }
                });
                value = subValues.join(" , ");
              }
              if (this.isDate(header)) value = this.toJDate(value);

              if (this.isBoolean(header)) {
                value = this.getBooleanFilterItems(header).find((o) => o.value == value).text;
              }

              appliedFilters.push({
                text: header.text,
                typeText: filterTypeText,
                value: value,
              });
            });
      }

      return appliedFilters;
    },
  },
  watch: {
    items() {
      this.tableItems = this.items;
    },
    apiUrl() {
      if (this.serverProcessing) this.d.getDataFromApi();
    },
    options: {
      handler() {
        this.$emit("update:options", this.options);
        if (this.serverProcessing) this.d.getDataFromApi();
      },
      deep: true,
    },
    filters: {
      handler() {
        this.options.page = 1;
        if (this.serverProcessing) this.d.getDataFromApi();
      },
      deep: true,
    },
    search() {
      if (this.serverProcessing) this.d.getDataFromApi();
    },
  },
  created() {
    this.d.getDataFromApi = _.debounce(this.getDataFromApi, 500);
  },
  mounted() {
    if (this.serverProcessing) this.d.getDataFromApi();

    this.EventBus.$on('fetch-data', () => {
      this.getDataFromApi();
    });
  },
  beforeDestroy() {
    this.EventBus.$off('fetch-data');
  },

  methods: {
    getBooleanFilterItems(header) {
      return [
        {
          text: header.text,
          value: true,
        },
        {
          text: "غیر " + header.text,
          value: false,
        },
      ];
    },
    getSelectItemValue(header, item) {
      let value = header.items.filter((o) => o.value == this.getItemValue(item, header.value));
      if (value.length) return value[0].text;
      return this.getItemValue(item, header.value);
    },
    getRowNumber(item) {
      let datatable = this.$refs.datatable;
      if (datatable) {
        let prev_page = (this.options.page - 1) * this.options.itemsPerPage;
        return datatable.$children[0].computedItems.indexOf(item) + 1 + prev_page;
      } else {
        return "";
      }
    },
    hasRangeFilter(header) {
      return header.showRangeFilter || this.isNumber(header) || this.isDate(header) || this.isTime(header);
    },
    emitFilter(key, value) {
      let newFilters = {...this.filters};
      newFilters[key] = value;
      this.$emit("update:filters", newFilters);
    },
    print() {
      if (this.isDialog) {
        $(".v-application--wrap")[0].style.display = "none";
      }

      this.$store.commit("setIsPrinting", true);
      this.$nextTick(() => {
        print();
        this.$store.commit("setIsPrinting", false);
        if (this.isDialog) {
          $(".v-application--wrap")[0].style.display = "block";
        }
      });
    },
    clearFilters(header) {
      let newFilters = {...this.filters};
      for (const filterType of this.filterTypes) {
        newFilters[filterType.getKey(header.value)] = "";
      }
      this.$emit("update:filters", newFilters);
    },
    clearSpecificFilters(text) {
      let this_header = null;
      for (let header of this.headers) {
        if (header.text == text) {
          this_header = header;
        }
      }
      let newFilters = {...this.filters};
      for (const filterType of this.filterTypes) {
        newFilters[filterType.getKey(this_header.value)] = "";
      }
      this.$emit("update:filters", newFilters);
    },
    clearAllFilters() {
      let newFilters = {...this.filters};
      Object.keys(newFilters).forEach((key) => {
        newFilters[key] = "";
      });
      this.$emit("update:filters", newFilters);
    },
    getFilterField(header) {
      if (this.isDate(header)) return "date";
      if (this.isTime(header)) return "mtime";
      if (this.isNumber(header)) return "money";
      return "v-text-field";
    },
    isDate(header) {
      return header.type == "date";
    },
    isDatetime(header) {
      return header.type == "datetime";
    },
    isTime(header) {
      return header.type == "time";
    },
    isSelect(header) {
      return header.items != undefined;
    },
    isNumber(header) {
      let flag;
      if (header.type == "text") flag = false;
      else if (header.type == "numeric") flag = true;
      else flag = this.numericValues.includes(header.value);
      if (flag) header.align = "center";
      return flag;
    },
    getNumericClasses(item, valueKey) {
      let value = this.getItemValue(item, valueKey);
      if (value < 0) return "text-red-600";
      return "text-gray-800";
    },
    isBoolean(header) {
      let flag = false;
      if (header.type == "boolean") flag = true;
      else flag = this.booleanValues.includes(header.value);
      if (flag) header.align = "center";
      return flag;
    },
    getItemSlot(value) {
      return `item.${value}`;
    },
    getHeaderSlot(value) {
      return `header.${value}`;
    },
    getOrdering() {
      const {sortBy, sortDesc} = this.options;
      let ordering;
      if (sortBy && sortBy.length === 1 && sortDesc.length === 1) {
        ordering = `${sortDesc[0] ? "-" : ""}${sortBy[0].replaceAll(".", "__")}`;
      }

      return ordering;
    },
    getDataFromApi() {
      this.loading = true;
      const {page, itemsPerPage} = this.options;

      let limit = itemsPerPage;
      let offset = itemsPerPage * (page - 1);

      if (limit == -1) {
        limit = 5000;
        offset = 0;
      }

      this.request({
        url: this.endpoint(this.apiUrl),
        method: "get",
        params: {
          limit: limit,
          offset: offset,
          ordering: this.getOrdering(),
          search: this.search,
          ...this.getFilters(),
        },
        success: (data) => {
          this.$emit("update:previousApiData", this.currentApiData);
          this.$emit("update:currentApiData", data);

          if (this.serverProcessing) {
            this.totalItems = data.count;
          } else {
            this.totalItems = undefined;
          }
          this.apiResponse = data;
          this.tableItems = data.results;
          this.loading = false;
        },
      });
    },
    getFilters() {
      let filters = {};
      for (let filterKey of Object.keys(this.filters)) {
        let filterValue = this.filters[filterKey];
        const header = this.headers.find((o) => o.value == filterKey);
        if (header && this.isDate(header)) {
          if (!/^\d{4}\-\d{2}\-\d{2}$/.test(filterValue)) {
            filterValue = undefined;
          }
        }
        filters[filterKey.replaceAll(".", "__")] = filterValue;
      }
      return filters;
    },
    getExportUrl(outputFormat) {
      let exportUrl = this.exportUrl || this.apiUrl;
      let url = "";
      if (exportUrl.includes("?")) {
        url = exportUrl.replace("?", `/${outputFormat}?`);
      } else {
        url = `${exportUrl}${exportUrl.endsWith("/") ? "" : "/"}${outputFormat}?`;
      }
      url = this.endpoint(url);
      return url;
    },
    exportTo(outputFormat) {
      this.$store.commit("setIsPrinting", true);
      this.$nextTick(() => {
        if (this.serverProcessing || this.serverExport) {
          let url = this.getExportUrl(outputFormat);
          if (this.selectedItems.length) {
            url += "id__in=" + this.selectedItems.map((o) => o.id).join(",");
          } else {
            let filters = {
              ...this.filters,
              search: this.search,
              ordering: this.getOrdering(),
            };

            Object.keys(filters).forEach((k) => {
              if (filters[k] != undefined) {
                url += k.replaceAll(".", "__") + "=" + filters[k] + "&";
              }
            });
            url += `headers=${JSON.stringify(this.headers.filter((o) => o.hideInExport != true))}&`;
            url += `applied_filters=${JSON.stringify(this.appliedFilters)}&`;
          }
          if (url[url.length - 1] != "&") url += "&";
          url += "token=" + this.token;

          this.downloadUrl(url);
        } else {
          if (outputFormat == "html") {
            this.print();
          } else if (outputFormat == "pdf") {
            this.print();
            return;
            let doc = new jsPDF();

            doc.html(document.body, {
              callback: function (doc) {
                doc.page = 1;
                doc.save();
              },
            });
          } else if (outputFormat == "xlsx") {
            let workbook = XLSX.utils.table_to_book(this.$refs.datatable.$el);
            let wb = workbook;

            if (!wb.Workbook) wb.Workbook = {};
            if (!wb.Workbook.Views) wb.Workbook.Views = [];
            if (!wb.Workbook.Views[0]) wb.Workbook.Views[0] = {};
            wb.Workbook.Views[0].RTL = true;
            XLSX.writeFile(workbook, "export.xlsx");
          }
        }
        this.$store.commit("setIsPrinting", false);
      });
    },
    getItemValue(item, dotNotationString) {
      let value = dotNotationString.split(".").reduce((o, i) => {
        if (o) return o[i];
        return null;
      }, item);

      return value;
    },
  },
};
</script>

<style>
/* Custom Vuetify overrides to work with Tailwind */
.v-data-table {
  @apply w-full;
}

.v-data-table > .v-data-table__wrapper > table {
  @apply w-full border-collapse;
}

.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  @apply px-4 py-3 text-xs font-medium text-gray-700 bg-gray-50 text-center;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  @apply px-4 py-2 text-xs text-gray-700 border-t border-gray-100;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  @apply bg-gray-50;
}

.v-data-table--fixed-header > .v-data-table__wrapper {
  @apply max-h-[calc(100vh-200px)];
}

.v-data-table__progress {
  @apply h-0.5 bg-primary-500;
}

.v-menu__content {
  @apply shadow-lg rounded-lg border border-gray-200 overflow-hidden;
}

.v-text-field.v-text-field--enclosed .v-text-field__details {
  @apply mb-0;
}

.v-input__slot {
  @apply min-h-[40px] shadow-none;
}

.v-text-field--outlined fieldset {
  @apply border-gray-200;
}

.v-text-field--outlined.v-input--is-focused fieldset {
  @apply border-primary-500 border;
}

.v-list-item--link:hover {
  @apply bg-gray-50;
}

.nowrap {
  @apply whitespace-nowrap;
}

@page {
  size: auto;
  margin: 0mm;
}

table {
  page-break-inside: auto;
}

tr {
  page-break-inside: avoid;
  page-break-after: auto;
}

thead {
  display: table-header-group;
}

tfoot {
  display: table-footer-group;
}
</style>